import type { ApiEnvelope, ApiProblem } from "@nutrayan/api-types";

/** Minimal token shape the client needs for the refresh flow (mirrors auth `TokenPair`). */
export interface AuthTokens {
  accessToken?: string;
  refreshToken?: string;
  expiresInSeconds?: number;
  tokenType?: string;
}

export interface ApiClientConfig {
  baseUrl: string;
  tenantCode?: string;
  getAuthToken?: () => string | undefined | Promise<string | undefined>;
  getCartToken?: () => string | undefined;
  getRequestId?: () => string;
  defaultLocale?: string;
  /** Current refresh token, if a session exists. Enables the 401 → refresh → retry flow. */
  getRefreshToken?: () => string | undefined;
  /**
   * When true, the refresh flow is attempted even without a body refresh token —
   * the backend reads the HttpOnly `refresh_token` cookie instead (BLK-API-014).
   * Same-origin fetches send cookies by default, so no credentials config needed.
   */
  cookieRefresh?: boolean;
  /**
   * Extra headers applied to every request (evaluated per call). Used by the admin
   * app to send `X-Tenant-Override` when a bootstrap super admin scopes the session
   * to a storefront tenant (BLK-API-015).
   */
  getExtraHeaders?: () => Record<string, string> | undefined;
  /** Called with fresh tokens after a successful refresh so the app updates its store. */
  onAuthRefreshed?: (tokens: AuthTokens) => void;
  /**
   * Called exactly once when a session that HAD auth can no longer be refreshed
   * (refresh token missing/expired/rejected). The app must clear all auth state,
   * show a single "session expired" message, and redirect to login. The client
   * guarantees single invocation per expiry burst; the handler should still be
   * idempotent.
   */
  onAuthCleared?: () => void;
}

/**
 * Auth endpoints must never trigger the refresh-and-retry flow: a 401 from
 * login/verify/refresh itself is a normal credential failure, and refreshing on
 * `/auth/refresh` would recurse. Matched against the request path.
 */
function isAuthEndpoint(path: string): boolean {
  return /\/api\/v1\/auth\//.test(path);
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ApiProblem;
  readonly traceId?: string;
  /**
   * True when this 401 triggered a session expiry (refresh impossible/failed).
   * Callers should NOT show their own error toast for these — a single global
   * "session expired" message + redirect is shown centrally.
   */
  readonly isAuthExpiry: boolean;

  constructor(status: number, problem: ApiProblem, isAuthExpiry = false) {
    super(problem.title ?? problem.detail ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.isAuthExpiry = isAuthExpiry;
    if (problem.traceId !== undefined) this.traceId = problem.traceId;
  }
}

/** True for the 401 that accompanies a session expiry — suppress local error toasts. */
export function isAuthExpiryError(error: unknown): boolean {
  return error instanceof ApiError && error.isAuthExpiry;
}

export class NetworkError extends Error {
  override readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "NetworkError";
    if (cause !== undefined) this.cause = cause;
  }
}

function defaultRequestId(): string {
  // Avoid crypto.randomUUID dependence for Edge runtimes that may polyfill differently.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  idempotencyKey?: string;
  locale?: string;
  /** When true, return the raw response instead of unwrapping `{data, meta}`. */
  raw?: boolean;
}

export function createApiClient(config: ApiClientConfig) {
  const {
    baseUrl,
    tenantCode = "nutrayan",
    getAuthToken,
    getCartToken,
    getRequestId = defaultRequestId,
    defaultLocale = "en-IN",
    getRefreshToken,
    cookieRefresh = false,
    getExtraHeaders,
    onAuthRefreshed,
    onAuthCleared,
  } = config;

  // Single-flight refresh: concurrent 401s share one in-flight refresh promise
  // so the refresh endpoint is hit at most once per expiry burst. Reset to null
  // once settled so a later, genuine expiry can refresh again.
  let refreshInFlight: Promise<AuthTokens | null> | null = null;
  // Ensures onAuthCleared fires once per expiry burst, not once per failed request.
  let authClearedForBurst = false;

  async function refreshTokens(): Promise<AuthTokens | null> {
    const refreshToken = getRefreshToken?.();
    if (!refreshToken && !cookieRefresh) return null;
    if (!refreshInFlight) {
      refreshInFlight = (async (): Promise<AuthTokens | null> => {
        try {
          const res = await fetch(joinUrl(baseUrl, "/api/v1/auth/refresh"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Tenant-Code": tenantCode,
              "X-Request-ID": getRequestId(),
            },
            // Body token when we hold one; otherwise the HttpOnly cookie carries it.
            body: JSON.stringify(refreshToken ? { refreshToken } : {}),
          });
          if (!res.ok) return null;
          const payload: unknown = await res.json().catch(() => null);
          const tokens = unwrap<AuthTokens>(payload);
          if (!tokens?.accessToken) return null;
          onAuthRefreshed?.(tokens);
          return tokens;
        } catch {
          return null;
        }
      })();
      // Clear the slot once settled, regardless of outcome.
      void refreshInFlight.finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  }

  /** Clear the session exactly once for a burst of concurrent 401s. */
  function triggerAuthCleared() {
    if (authClearedForBurst) return;
    authClearedForBurst = true;
    onAuthCleared?.();
  }

  async function request<T>(
    path: string,
    options: ApiRequestOptions = {},
    retried = false,
  ): Promise<T> {
    const { body, idempotencyKey, locale, raw, headers, ...rest } = options;

    const finalHeaders = new Headers(headers ?? {});
    finalHeaders.set("Accept", "application/json");
    finalHeaders.set("Accept-Language", locale ?? defaultLocale);
    finalHeaders.set("X-Tenant-Code", tenantCode);
    finalHeaders.set("X-Request-ID", getRequestId());

    const extra = getExtraHeaders?.();
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        if (v) finalHeaders.set(k, v);
      }
    }

    if (body !== undefined && !finalHeaders.has("Content-Type")) {
      finalHeaders.set("Content-Type", "application/json");
    }

    const token = getAuthToken ? await getAuthToken() : undefined;
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);

    const cartToken = getCartToken?.();
    if (cartToken) finalHeaders.set("Cart-Token", cartToken);

    if (idempotencyKey) finalHeaders.set("Idempotency-Key", idempotencyKey);

    const init: RequestInit = {
      ...rest,
      headers: finalHeaders,
    };
    if (body !== undefined) init.body = JSON.stringify(body);

    let res: Response;
    try {
      res = await fetch(joinUrl(baseUrl, path), init);
    } catch (cause) {
      throw new NetworkError("Network request failed", cause);
    }

    const contentType = res.headers.get("Content-Type") ?? "";
    const isJson = contentType.includes("application/json") || contentType.includes("problem+json");
    const payload: unknown =
      isJson && res.status !== 204 ? await res.json().catch(() => null) : null;

    // An /api response that is HTML means the same-origin proxy (Next rewrites
    // or the production reverse proxy) is NOT forwarding /api/* to the backend —
    // the SPA shell was served instead. Surface that precisely rather than a
    // confusing "Invalid credentials" or null-token error downstream.
    if (!isJson && res.status !== 204 && contentType.includes("text/html")) {
      throw new NetworkError(
        `API request to ${path} returned HTML, not JSON — the backend proxy is not ` +
          `forwarding /api/* on this origin. Check the reverse-proxy / rewrites config.`,
      );
    }

    // 401 on an authenticated request. `token` (hadAuth) ensures we only run this
    // for requests that carried a session — a 401 with no token is "not signed in"
    // and is left for the caller (e.g. guest checkout redirects to login itself).
    // Auth endpoints (login/verify/refresh) are skipped: a 401 there is a normal
    // credential failure, not an expiry.
    if (res.status === 401 && token && !isAuthEndpoint(path)) {
      if (!retried) {
        // First 401: single-flight refresh, then retry the original request once.
        const refreshed = await refreshTokens();
        if (refreshed?.accessToken) {
          // getAuthToken now returns the new token (updated in the refresh
          // callback). Retry once; `retried` blocks any further refresh → no loop.
          return request<T>(path, options, true);
        }
      }
      // Refresh unavailable/failed, or the retried request still 401'd → the
      // session cannot be recovered. Clear it exactly once for this burst.
      triggerAuthCleared();
      const problem: ApiProblem = isProblem(payload) ? payload : { status: 401 };
      throw new ApiError(401, problem, true);
    }

    if (!res.ok) {
      const problem: ApiProblem = isProblem(payload) ? payload : { status: res.status };
      throw new ApiError(res.status, problem);
    }

    // A successful authenticated response means a live session — re-arm the
    // one-shot logout guard so a future, genuine expiry can log out again.
    if (token) authClearedForBurst = false;

    if (raw) return payload as T;
    return unwrap<T>(payload);
  }

  return { request };
}

function joinUrl(base: string, path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const sep = base.endsWith("/") || path.startsWith("/") ? "" : "/";
  return `${base}${sep}${path}`;
}

function isProblem(value: unknown): value is ApiProblem {
  return typeof value === "object" && value !== null;
}

function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === "object" && "data" in (payload as object)) {
    return (payload as ApiEnvelope<T>).data;
  }
  return payload as T;
}
